import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg, reportClientError } from "@/lib/api";
import { uploadFileSmart, uploadLimitError } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { EmptyState, PageHeader } from "@/components/shared";
import { PAGE_HELP } from "@/components/help";
import { Camera, Images, PawPrint, Pencil, Trash2, Upload } from "lucide-react";

const BLANK = { name: "", species: "Dog", breed: "", age: "", sex: "", weight: "", status: "owned", traits: "", triggers: "", comfortRituals: "", medicalNotes: "", vetContact: "" };
const STATUSES = ["owned", "foster", "rescue", "senior", "other"];
const SEXES = ["Male", "Female", "Male (neutered)", "Female (spayed)", "Unknown"];

// Species -> common breeds. "Mixed / Mutt" and "Other" are always available;
// "Other" reveals a free-text input so no pet is ever left out.
const SPECIES_BREEDS = {
  Dog: ["Labrador Retriever", "Golden Retriever", "German Shepherd", "French Bulldog", "Bulldog", "Poodle", "Beagle", "Rottweiler", "Dachshund", "Corgi", "Australian Shepherd", "Yorkshire Terrier", "Boxer", "Husky", "Great Dane", "Doberman", "Shih Tzu", "Border Collie", "Chihuahua", "Pomeranian", "Cavalier King Charles Spaniel", "Pit Bull / Staffordshire", "Bernese Mountain Dog", "Maltese", "Cane Corso", "Shiba Inu"],
  Cat: ["Domestic Shorthair", "Domestic Longhair", "Maine Coon", "Siamese", "Ragdoll", "Persian", "Bengal", "Sphynx", "British Shorthair", "Abyssinian", "Scottish Fold", "Russian Blue", "Norwegian Forest Cat", "Devon Rex", "Burmese", "Tabby"],
  Bird: ["Parakeet / Budgie", "Cockatiel", "Conure", "African Grey Parrot", "Lovebird", "Macaw", "Cockatoo", "Canary", "Finch", "Amazon Parrot"],
  Rabbit: ["Holland Lop", "Netherland Dwarf", "Mini Rex", "Lionhead", "Flemish Giant", "Dutch", "Mini Lop", "English Angora"],
  "Small mammal": ["Guinea Pig", "Hamster", "Gerbil", "Chinchilla", "Ferret", "Rat", "Mouse", "Hedgehog"],
  Reptile: ["Bearded Dragon", "Leopard Gecko", "Crested Gecko", "Ball Python", "Corn Snake", "Turtle", "Tortoise", "Iguana"],
  Fish: ["Betta", "Goldfish", "Guppy", "Angelfish", "Tetra", "Cichlid", "Koi"],
  Horse: ["Quarter Horse", "Thoroughbred", "Arabian", "Appaloosa", "Paint Horse", "Morgan", "Pony", "Draft"],
  Other: [],
};
const SPECIES_LIST = Object.keys(SPECIES_BREEDS);
const BREED_MIXED = "Mixed / Mutt";
const BREED_OTHER = "Other";

const useObjectUrl = () => {
  const urls = useRef([]);
  useEffect(() => {
    const tracked = urls.current; // snapshot the ref for a safe cleanup closure
    return () => tracked.forEach((u) => URL.revokeObjectURL(u));
  }, []);
  return useCallback((blob) => { const u = URL.createObjectURL(blob); urls.current.push(u); return u; }, []);
};

export default function Pets() {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  // Photo gallery state
  const [galleryPet, setGalleryPet] = useState(null);
  const [photos, setPhotos] = useState(null); // metadata list
  const [photoUrls, setPhotoUrls] = useState({}); // id -> object URL
  const [avatars, setAvatars] = useState({}); // petId -> object URL
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const makeUrl = useObjectUrl();

  const fetchBlobUrl = useCallback(async (fileId) => {
    const { data } = await api.get(`/uploads/${fileId}`, { responseType: "blob" });
    return makeUrl(data);
  }, [makeUrl]);

  const loadAvatars = useCallback(async (petList) => {
    for (const p of petList) {
      try {
        const { data: metas } = await api.get(`/pet-photos/${p.id}`);
        const firstImg = metas.find((m) => (m.contentType || "").startsWith("image/"));
        if (firstImg) {
          const url = await fetchBlobUrl(firstImg.id);
          setAvatars((a) => ({ ...a, [p.id]: url }));
        }
      } catch (e) { console.debug(`No photos yet for pet ${p.id}:`, e?.message); }
    }
  }, [fetchBlobUrl]);

  const openGallery = async (pet) => {
    setGalleryPet(pet);
    setPhotos(null);
    try {
      const { data: metas } = await api.get(`/pet-photos/${pet.id}`);
      setPhotos(metas);
      for (const m of metas) {
        if ((m.contentType || "").startsWith("image/") && !photoUrls[m.id]) {
          fetchBlobUrl(m.id).then((url) => setPhotoUrls((u) => ({ ...u, [m.id]: url }))).catch((err) => console.warn(`Photo ${m.id} failed to load:`, err?.message));
        }
      }
    } catch (e) { toast.error(errMsg(e)); setPhotos([]); }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    e.target.value = "";
    if (!files.length || !galleryPet) return;
    // Enforce limits up front (photos auto-compress; videos up to 200 MB via chunked upload)
    const ok = files.filter((f) => {
      const err = uploadLimitError(f);
      if (err) { toast.error(err); return false; }
      return true;
    });
    if (!ok.length) return;
    setUploading(true);
    try {
      for (const f of ok) {
        if (f.size > 24 * 1024 * 1024) toast.info(`Uploading "${f.name}" — large videos take a little longer...`);
        await uploadFileSmart(f, { petId: galleryPet.id, silent: true });
      }
      toast.success(`${ok.length} photo${ok.length > 1 ? "s" : ""} added to ${galleryPet.name}'s gallery.`);
      openGallery(galleryPet);
      loadAvatars([galleryPet]);
    } catch (e2) {
      reportClientError("pets.photo_upload", e2, { petId: galleryPet.id });
      toast.error(errMsg(e2));
    } finally { setUploading(false); }
  };

  const deletePhoto = async (m) => {
    try {
      await api.delete(`/uploads/${m.id}`);
      toast.success("Photo removed.");
      openGallery(galleryPet);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pet-profiles");
      setPets(data);
      loadAvatars(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [loadAvatars]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...BLANK, breedChoice: "", breedCustom: "" }); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    const species = SPECIES_LIST.includes(p.species) ? p.species : "Other";
    const list = SPECIES_BREEDS[species] || [];
    const breed = p.breed || "";
    let breedChoice = "", breedCustom = "";
    if (breed) {
      if (list.includes(breed) || breed === BREED_MIXED) breedChoice = breed;
      else { breedChoice = BREED_OTHER; breedCustom = breed; }
    }
    setForm({ ...p, species, breedChoice, breedCustom, age: p.age ?? "", weight: p.weight ?? "", traits: (p.traits || []).join(", "), triggers: (p.triggers || []).join(", "), comfortRituals: (p.comfortRituals || []).join(", ") });
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.species) { toast.error("Name and species are required."); return; }
    setBusy(true);
    const breed = form.breedChoice === BREED_OTHER ? (form.breedCustom || "").trim() : (form.breedChoice || "");
    const payload = {
      name: form.name, species: form.species, breed, sex: form.sex, status: form.status,
      age: form.age === "" ? null : Number(form.age), weight: form.weight === "" ? null : Number(form.weight),
      traits: form.traits.split(",").map((s) => s.trim()).filter(Boolean),
      triggers: form.triggers.split(",").map((s) => s.trim()).filter(Boolean),
      comfortRituals: form.comfortRituals.split(",").map((s) => s.trim()).filter(Boolean),
      medicalNotes: form.medicalNotes, vetContact: form.vetContact,
    };
    try {
      if (editId) { await api.patch(`/pet-profiles/${editId}`, payload); toast.success("Pet profile updated."); }
      else { await api.post("/pet-profiles", payload); toast.success(`${form.name} added. Welcome!`); }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Archive ${p.name}'s profile? (soft delete)`)) return;
    try { await api.delete(`/pet-profiles/${p.id}`); toast.success("Profile archived."); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader title="My Pets" help={PAGE_HELP.pets} subtitle="Profiles power the Bond Score, rituals, and vet-ready reports.">
        <Button onClick={openCreate} className="rounded-xl" data-testid="add-pet-button">Add pet</Button>
      </PageHeader>

      {loading ? <p className="text-muted-foreground text-sm">Loading...</p> : !pets.length ? (
        <EmptyState icon={PawPrint} title="No pets yet" hint="Add your first pet to begin." action={<Button onClick={openCreate} className="rounded-xl">Add my pet</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {pets.map((p) => (
            <Card key={p.id} className="rounded-2xl hover:shadow-[0_18px_50px_-30px_rgba(0,0,0,0.35)] transition-shadow duration-200" data-testid={`pet-card-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {avatars[p.id] ? (
                      <img src={avatars[p.id]} alt={p.name} className="w-10 h-10 rounded-xl object-cover" data-testid={`pet-avatar-${p.id}`} />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center"><PawPrint className="w-5 h-5 text-primary" /></div>
                    )}
                    <div>
                      <p className="font-semibold">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.species}{p.breed ? ` · ${p.breed}` : ""}{p.age ? ` · ${p.age} yrs` : ""}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="capitalize border-0">{p.status}</Badge>
                </div>
                {(p.traits?.length > 0 || p.triggers?.length > 0) && (
                  <div className="mt-3 space-y-1.5">
                    {p.traits?.length > 0 && <p className="text-xs"><span className="text-muted-foreground">Traits:</span> {p.traits.join(", ")}</p>}
                    {p.triggers?.length > 0 && <p className="text-xs"><span className="text-muted-foreground">Triggers:</span> {p.triggers.join(", ")}</p>}
                    {p.comfortRituals?.length > 0 && <p className="text-xs"><span className="text-muted-foreground">Comforts:</span> {p.comfortRituals.join(", ")}</p>}
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => openGallery(p)} data-testid={`pet-photos-button-${p.id}`}><Images className="w-3.5 h-3.5 mr-1" /> Photos</Button>
                  <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => openEdit(p)} data-testid={`edit-pet-button-${p.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="ghost" className="rounded-lg text-destructive hover:text-destructive" onClick={() => remove(p)} data-testid={`delete-pet-button-${p.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Archive</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!galleryPet} onOpenChange={(o) => !o && setGalleryPet(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pet-gallery-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="w-4 h-4 text-primary" /> {galleryPet?.name}'s Photos</DialogTitle></DialogHeader>
          <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={uploadPhotos} data-testid="pet-photo-input" />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Photos make records personal and vet-ready. Free on every plan.</p>
            <Button size="sm" className="rounded-lg" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pet-photo-upload-button">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading..." : "Add photos"}
            </Button>
          </div>
          {photos === null ? (
            <div className="grid grid-cols-3 gap-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}</div>
          ) : photos.length === 0 ? (
            <div className="text-center py-10">
              <Images className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No photos yet — add the first one!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="pet-photo-grid">
              {photos.map((m) => (
                <div key={m.id} className="relative group aspect-square rounded-xl overflow-hidden bg-secondary">
                  {photoUrls[m.id] ? (
                    <img src={photoUrls[m.id]} alt={m.filename} className="w-full h-full object-cover" />
                  ) : (
                    <Skeleton className="w-full h-full" />
                  )}
                  <button
                    onClick={() => deletePhoto(m)}
                    className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-black/70 text-white rounded-lg p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                    aria-label="Delete photo"
                    data-testid={`pet-photo-delete-${m.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="pet-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit pet profile" : "Add a pet"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Name *</Label><Input value={form.name} onChange={set("name")} data-testid="pet-name-input" /></div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label>Species *</Label>
              <Select value={form.species} onValueChange={(v) => setForm((f) => ({ ...f, species: v, breedChoice: "", breedCustom: "" }))}>
                <SelectTrigger data-testid="pet-species-select"><SelectValue placeholder="Choose species" /></SelectTrigger>
                <SelectContent>{SPECIES_LIST.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label>Breed</Label>
              <Select value={form.breedChoice || ""} onValueChange={(v) => setForm((f) => ({ ...f, breedChoice: v }))}>
                <SelectTrigger data-testid="pet-breed-select"><SelectValue placeholder="Choose breed" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {(SPECIES_BREEDS[form.species] || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  <SelectItem value={BREED_MIXED}>{BREED_MIXED}</SelectItem>
                  <SelectItem value={BREED_OTHER}>{BREED_OTHER}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label>Sex</Label>
              <Select value={form.sex || ""} onValueChange={(v) => setForm((f) => ({ ...f, sex: v }))}>
                <SelectTrigger data-testid="pet-sex-select"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{SEXES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.breedChoice === BREED_OTHER && (
              <div className="space-y-1.5 col-span-2"><Label>Tell us the breed</Label><Input value={form.breedCustom || ""} onChange={(e) => setForm((f) => ({ ...f, breedCustom: e.target.value }))} placeholder="e.g. Lagotto Romagnolo" data-testid="pet-breed-custom-input" /></div>
            )}
            <div className="space-y-1.5"><Label>Age (years)</Label><Input type="number" value={form.age} onChange={set("age")} data-testid="pet-age-input" /></div>
            <div className="space-y-1.5"><Label>Weight (lbs)</Label><Input type="number" value={form.weight} onChange={set("weight")} data-testid="pet-weight-input" /></div>
            <div className="space-y-1.5 col-span-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="pet-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Traits (comma-separated)</Label><Input value={form.traits} onChange={set("traits")} placeholder="gentle, food motivated" data-testid="pet-traits-input" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Triggers (comma-separated)</Label><Input value={form.triggers} onChange={set("triggers")} placeholder="thunder, vacuum" data-testid="pet-triggers-input" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Comfort rituals (comma-separated)</Label><Input value={form.comfortRituals} onChange={set("comfortRituals")} placeholder="soft blanket, quiet corner" data-testid="pet-comforts-input" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Medical notes (observations only)</Label><Textarea rows={2} value={form.medicalNotes} onChange={set("medicalNotes")} data-testid="pet-medical-input" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Vet contact</Label><Input value={form.vetContact} onChange={set("vetContact")} data-testid="pet-vet-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} className="rounded-xl" data-testid="pet-save-button">{busy ? "Saving..." : "Save pet"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
